import React from 'react';
import {
    Page,
    Text,
    View,
    Document,
    StyleSheet,
    Image,
    Font
} from '@react-pdf/renderer';
import logo from '@/assets/seaflashlogo.png';
import shipImage from '@/assets/reportimg.png';

Font.register({
    family: 'Helvetica-Bold',
    fonts: [
        {
            src: 'https://fonts.gstatic.com/s/helvetica/helveticabold.woff',
            fontWeight: 'bold'
        }
    ]
});

const styles = StyleSheet.create({
    page: {
        padding: 20,
        fontSize: 9,
        fontFamily: 'Helvetica',
        backgroundColor: '#fff'
    },
    chartPage: {
        padding: 20,
        fontSize: 9,
        fontFamily: 'Helvetica',
        backgroundColor: '#fff',
        flexDirection: 'column',
        minHeight: '100vh'
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    borderBox: {
        position: 'absolute',
        top: 15,
        left: 15,
        right: 15,
        bottom: 15,
        border: '1 solid #ccc',
        borderRadius: 6,
        zIndex: -1
    },
    centeredImageContainer: {
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 16,
    },
    shipImage: {
        width: 300,
        height: 300,
        objectFit: 'cover',
        borderRadius: 8
    },
    logo: {
        width: 60,
        height: 60
    },
    title: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#007f66',
        fontStyle: 'italic',
        marginRight: 15
    },
    sectionTitle: {
        fontSize: 10,
        fontWeight: 'bold',
        marginBottom: 8,
    },
    detailRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 12,
        marginBottom: 20
    },
    detailBox: {
        width: '50%',
        border: '1 solid #ccc',
        borderRadius: 6,
        padding: 10,
        backgroundColor: '#fdfdfd'
    },
    titleRow: {
        flexDirection: 'row',
        alignItems: 'baseline',
        marginBottom: 4
    },
    chartTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        color: '#007f66',
        marginBottom: 2
    },
    chartSubtitle: {
        fontSize: 8,
        color: '#666',
        marginLeft: 8,
    },
    chartDescription: {
        fontSize: 8,
        color: '#555',
        marginBottom: 8,
    },
    summaryContainer: {
        marginTop: 8,
        marginBottom: 12
    },
    summaryGrid: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
    },
    summaryCard: {
        width: '24%',
        padding: 8,
        border: '1 solid #ddd',
        borderRadius: 6,
        backgroundColor: '#f8f9fa',
        marginBottom: 8
    },
    summaryCardTitle: {
        fontSize: 8,
        fontWeight: 'bold',
        color: '#007f66',
        borderBottom: '0.5 solid #007f66',
        marginBottom: 4,
        paddingBottom: 2
    },
    entryList: {
        gap: 2
    },
    entryRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 2
    },
    entryLabel: {
        fontSize: 7,
        color: '#333'
    },
    entryValue: {
        fontSize: 7,
        fontWeight: 'bold',
        color: '#007f66'
    },
    box: {
        paddingVertical: 1,
        paddingHorizontal: 1,
    },
    row: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 2,
        lineHeight: 1
    },
    label: {
        fontWeight: 'bold',
        color: '#333'
    },
    reporttopic: {
        fontWeight: 'bold',
        color: '#6e6e6eff',
        fontSize: "14px"
    },
    chartImage: {
        width: '95%',
        height: "auto",
        marginLeft: 'auto',
        marginRight: 'auto',
        marginBottom: 4,
    },
    tableImage: {
        width: '100%',
        height: 'auto',
        objectFit: 'contain',
    },
    summaryImage: {
        marginTop: 6,
        width: '95%',
        marginLeft: 'auto',
        marginRight: 'auto',
        objectFit: 'contain',
        marginBottom: 4,
    },
    // Enhanced table styles
    tableContainer: {
        marginTop: 10,
        marginBottom: 12,
        border: '1 solid #ddd',
        borderRadius: 6,
        backgroundColor: '#fdfdfd'
    },
    tableTitle: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#007f66',
        marginBottom: 6,
        textAlign: 'center'
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#007f66',
        paddingVertical: 6,
        paddingHorizontal: 10,
        borderTopLeftRadius: 6,
        borderTopRightRadius: 6
    },
    tableHeaderText: {
        color: '#fff',
        fontWeight: 'bold',
        fontSize: 9,
        textAlign: 'center'
    },
    tableHeaderDate: {
        width: '60%'
    },
    tableHeaderValue: {
        width: '40%'
    },
    tableRow: {
        flexDirection: 'row',
        paddingVertical: 6,
        paddingHorizontal: 12,
        borderBottom: '0.5 solid #eee'
    },
    tableRowAlternate: {
        backgroundColor: '#f8f9fa'
    },
    tableCellDate: {
        width: '60%',
        fontSize: 8,
        color: '#333'
    },
    tableCellValue: {
        width: '40%',
        fontSize: 8,
        fontWeight: 'bold',
        color: '#007f66',
        textAlign: 'center'
    },
    // Enhanced comments section
    commentsContainer: {
        marginTop: 10,
        border: '1 solid #ddd',
        borderRadius: 6,
        backgroundColor: '#fdfdfd',
        padding: 8
    },
    commentsTitle: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#007f66',
        marginBottom: 6,
        borderBottom: '1 solid #007f66',
        paddingBottom: 2
    },
    commentBox: {
        marginBottom: 6,
        padding: 6,
        backgroundColor: '#f8f9fa',
        borderRadius: 4,
        borderLeft: '3 solid #007f66'
    },
    commentMeta: {
        fontSize: 7,
        fontWeight: 'bold',
        color: '#555',
        marginBottom: 2
    },
    commentContent: {
        fontSize: 8,
        color: '#333',
        lineHeight: 1.3
    },
    containbox: {
        width: '95%',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    // Chart container with trimming support
    chartContainer: {
        marginBottom: 20,
        paddingBottom: 15,
        borderBottom: '1 solid #eee',
        breakInside: 'avoid'
    },
    chartSection: {
        marginBottom: 15
    },
    footer: {
        position: 'absolute',
        bottom: 15,
        left: 15,
        right: 15,
        fontSize: 10,
        color: '#fff',
        backgroundColor: '#0D9276',
        padding: 8,
        textAlign: 'center',
        borderRadius: 4
    },
    commentstitle: {
        fontSize: 8,
        fontWeight: 'bold',
        color: '#007f66',
        marginBottom: 6,
    },
    commentview: {
        width: '95%',
        height: 180,
        marginLeft: 'auto',
        marginRight: 'auto',
        objectFit: 'contain',
    }
});

const Footer = () => (
    <Text style={styles.footer} fixed>SEA FLASH · Brilliance In Every Flash</Text>
);

const ChartSection = ({ chart }) => {
    if (!chart) return null;
    console.log("afafafafafafafaf", chart)
    const hasTitle = chart.chartTitle || chart.chartSubtitle;
    const hasImages = chart.summaryImage || chart.chartImage || chart.tableImage;
    const hasComments = chart.commentImage;
    const hasDescription = chart.chartDescription;

    // Only render if there's actual content
    if (!hasTitle && !hasImages && !hasComments && !hasDescription) {
        return null;
    }

    return (
        <View style={styles.chartContainer} wrap={false}>
            <View style={styles.containbox}>
                {hasTitle && (
                    <View style={styles.titleRow}>
                        {chart.chartTitle && <Text style={styles.chartTitle}>{chart.chartTitle}</Text>}
                        {chart.chartSubtitle && <Text style={styles.chartSubtitle}>{chart.chartSubtitle}</Text>}
                    </View>
                )}

                {chart.chartDescription && (
                    <Text style={styles.chartDescription}>{chart.chartDescription}</Text>
                )}
            </View>

            {chart.summaryImage && (
                <Image style={styles.summaryImage} src={chart.summaryImage} />
            )}

            {chart.chartImage && (

                <Image style={styles.chartImage} src={chart.chartImage} fit="contain"
                />
            )}

            {chart.tableImage && (
                <Image style={styles.tableImage} src={chart.tableImage} />
            )}

            {chart.commentImage && (
                <View style={styles.commentview}>
                    <Text style={styles.commentstitle}>Comments</Text>
                    <Image style={styles.tableImage} src={chart.commentImage} />
                </View>
            )}


        </View>
    );
};

const SeaFlashPDF = ({ data }) => {
    if (!data) {
        return
    }
    const { shipDetail, engineDetail, chartdetails = [], shipimg, reportName = "" } = data;
    // Filter charts that have meaningful content for trimming
    const validCharts = chartdetails.filter(chart => {
        if (!chart) return false;
        const hasTitle = chart.chartTitle || chart.chartSubtitle;
        const hasImages = chart.summaryImage || chart.chartImage || chart.tableImage;
        const hasComments = chart.comments && chart.comments.length > 0;
        const hasDescription = chart.description;
        return hasTitle || hasImages || hasComments || hasDescription;
    });

    return (
        <Document>
            {/* First page - Always rendered with fixed structure */}
            <Page size="A4" orientation="portrait" style={styles.page}>
                <View style={styles.borderBox} fixed />
                <View style={styles.header} fixed>
                    <Image src={logo} style={styles.logo} />
                    <View>
                        <Text style={styles.reporttopic}>{reportName}</Text>
                    </View>
                    <Text style={styles.title}>SEA FLASH</Text>
                </View>

                <View style={styles.centeredImageContainer}>
                    <Image src={shipimg ? shipimg : shipImage} style={styles.shipImage} />
                </View>

                <View style={styles.detailRow}>
                    <View style={styles.detailBox}>
                        <Text style={styles.sectionTitle}>Ship Detail</Text>
                        <View style={styles.box}>
                            <View style={styles.row}><Text style={styles.label}>IMO Number:</Text><Text>{shipDetail.imoNumber}</Text></View>
                            <View style={styles.row}><Text style={styles.label}>Name:</Text><Text>{shipDetail.name}</Text></View>
                            <View style={styles.row}><Text style={styles.label}>Type:</Text><Text>{shipDetail.type}</Text></View>
                            <View style={styles.row}><Text style={styles.label}>Flag:</Text><Text>{shipDetail.flag}</Text></View>
                            <View style={styles.row}><Text style={styles.label}>Owner Code:</Text><Text>{shipDetail.ownerCode}</Text></View>
                        </View>
                    </View>

                    <View style={styles.detailBox}>
                        <Text style={styles.sectionTitle}>Engine Detail & Capacity</Text>
                        <View style={styles.box}>
                            <View style={styles.row}><Text style={styles.label}>Engine Type:</Text><Text>{engineDetail.type}</Text></View>
                            <View style={styles.row}><Text style={styles.label}>Engine Model:</Text><Text>{engineDetail.model}</Text></View>
                            <View style={styles.row}><Text style={styles.label}>Engine Power:</Text><Text>{engineDetail.power}</Text></View>
                            <View style={styles.row}><Text style={styles.label}>Gross Tonnage:</Text><Text>{engineDetail.grossTonnage}</Text></View>
                            <View style={styles.row}><Text style={styles.label}>Deadweight:</Text><Text>{engineDetail.deadweight}</Text></View>
                        </View>
                    </View>
                </View>

                {/* DUMMY */}

                <Footer />
            </Page>
            {validCharts.length > 0 && (
                <Page size="A4" orientation="portrait" style={styles.chartPage} wrap>
                    <View style={styles.borderBox} fixed />

                    <View style={styles.header} fixed>
                        <Image src={logo} style={styles.logo} />
                        <View>
                            <Text style={styles.reporttopic}>{reportName}</Text>
                        </View>
                        <Text style={styles.title}>SEA FLASH</Text>
                    </View>
                    {/* Render all valid charts continuously with trimming */}
                    {validCharts.map((chart, idx) => (
                        <ChartSection key={idx} chart={chart} />
                    ))}

                    <Footer />
                </Page>
            )}
        </Document>
    );
};

export default SeaFlashPDF;